import { BaseButton, DefaultButton, FontIcon, IProcessedStyleSet, Spinner } from 'office-ui-fabric-react';
import * as React from 'react';

import { Choice, RaisedEventStatus } from '../../models';
import { createStructuredSelector, IImmutableMap, mconnect } from '../../state/redux';
import { IStyledComponent, styledComponent } from '../../theme';
import { IChoiceNodeStyles, IComponentStateProps, IConnectedComponentProps, INodeListStyles } from '../../types';
import {
  getChoiceNodeClassNames,
  getChoiceNodeStyles,
  getOptionBtnStyles} from '../intentGraph.styles';
import { dispatchPushTargetNode } from '../reducers/dispatchers';
import { updateRaisedEventAction } from '../reducers/actions';
import { dispatchEditNodeChoiceSelections } from '../reducers/dispatchers';
import {
  getDynamicChoicesListSelector,
  getDynamicChoicesSelector,
  getIsInEditModeSelector,
  getNodeSelector,
  getSelectedChoiceSelector,
  getStaticChoicesSelector,
  isDiagnosticActionInProgressSelector,
  nodeRaisedEventsSelector,
  pauseForDiagnosticExecutionSelector
} from '../reducers/selectors.redux';
import strings from "../intentGraph.string.json";
import { getString } from '../constants/stringLoader';
import { ResourceKeys } from '../constants/LocalizedResourceKeys';

const mapStateToProps = createStructuredSelector({
  node: getNodeSelector,
  staticChoices: getStaticChoicesSelector,
  dynamicChoiceList: getDynamicChoicesListSelector,
  dynamicChoices: getDynamicChoicesSelector,
  selectedChoice: getSelectedChoiceSelector,
  isDiagnosticsActionInProgress: isDiagnosticActionInProgressSelector,
  isInEditMode: getIsInEditModeSelector,
  nodeRaisedEvents: nodeRaisedEventsSelector,
  pauseForDiagnosticExecution: pauseForDiagnosticExecutionSelector
});

const mapDispatchToProps = {
  dispatchPushTargetNode,
  dispatchEditNodeChoiceSelections,
  updateRaisedEventAction
};

type INodeChoicesProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps &
  IStyledComponent<INodeListStyles> &
  IConnectedComponentProps &
  IComponentStateProps;
  
class ChoiceNodeC extends React.PureComponent<INodeChoicesProps> {
  private clickedNode = -1;
  private lastClickedDynamicChoice = false;
  private firstRender = true;
  private waitingForRaisedEvent = false;
  private targetNodeValues: { [key: string]: IImmutableMap<Choice> | number | string | boolean } = {};
  constructor(props: INodeChoicesProps) {
    super(props);
  }

  state = {
    isPressed: false
  }

  public componentDidUpdate = (_prevProps: INodeChoicesProps): void => {
    if (this.props.nodeRaisedEvents && this.waitingForRaisedEvent) {
      const status = this.props.nodeRaisedEvents.get('status');
      if (status !== RaisedEventStatus.inProgress) {
        this.waitingForRaisedEvent = false;
        const eventSucceeded = this.props.nodeRaisedEvents.get('status') === RaisedEventStatus.success;
        const { uId, selectedChoice, isDynamicChoice, index } = this.targetNodeValues;
        this.props.dispatchPushTargetNode(
          uId as string,
          selectedChoice as IImmutableMap<Choice>,
          isDynamicChoice as boolean,
          index as number,
          this.props.node,
          this.props.callbackHandlers.LogHandler,
          this.props.callbackHandlers.IsUserSignedIn,
          eventSucceeded
        );
      }
    }
  };

  public render = (): React.ReactNode => {
    const classNames = getChoiceNodeClassNames(this.props.styles, {
      theme: this.props.theme,
      stylesFunc: this.props.stylesFunc
    });
    if (this.props.nodeRaisedEvents && this.props.nodeRaisedEvents.get('status') === RaisedEventStatus.inProgress) {
      return <Spinner styles={getChoiceNodeStyles(this.props).spinner} />;
    }
    return (
      <div className={classNames.choicesList} key={`${this.props.node.get('uId')}-choices`} aria-live='assertive' aria-atomic='false'>
        {this.props.dynamicChoices && this._getDynamicChoiceList(classNames)}
        {this._getStaticChoices(classNames)}
      </div>
    );
  };

  private _onChoiceBtnClick = (
    selectedChoice: IImmutableMap<Choice>,
    isDynamicChoice: boolean = false,
    isSelectedChoice: boolean,
    index?: number
  ) => (
    _event: React.MouseEvent<
      HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | BaseButton | HTMLSpanElement,
      MouseEvent
    >
  ): void => {

    this.setState({ isPressed: !this.state.isPressed });
    const target = _event?.target as HTMLElement|undefined;
    target?.blur();
    setImmediate(() => target?.focus());
    if(!this.props.pauseForDiagnosticExecution){
      const {uId} = this.props;
      this.clickedNode = index;
      this.firstRender = false;
      if (isSelectedChoice && !this.props.isInEditMode ) {
        this._onEditBarClick(_event);
      } else {
        this.lastClickedDynamicChoice = isDynamicChoice;
        let raiseEventName;
        let eventHold;
        if(!!selectedChoice){
          raiseEventName = selectedChoice.get('raiseEventName') || '';
          eventHold = !!selectedChoice.get('eventHold');
        }
        // raise event
        if (
          !!raiseEventName &&
          raiseEventName !== '' &&
          !!this.props.callbackHandlers.RaiseEventHandler &&
          typeof this.props.callbackHandlers.RaiseEventHandler === 'function'
        ) {
          if (!!eventHold) {
            this.props.updateRaisedEventAction(uId, raiseEventName, RaisedEventStatus.inProgress);
            this.targetNodeValues = {
              uId,
              isDynamicChoice,
              selectedChoice,
              index
            };
            this.waitingForRaisedEvent = true;
          }
          this.props.callbackHandlers.RaiseEventHandler(raiseEventName, null, uId);
        } 
        // dispatch next node if we shouldn't hold for raised event
        if(!eventHold){
          this.props.dispatchPushTargetNode(
            uId,
            selectedChoice,
            isDynamicChoice,
            index,
            this.props.node,
            this.props.callbackHandlers.LogHandler,
            this.props.callbackHandlers.IsUserSignedIn
          );
        }
      }
    }
  };

  private _onEditBarClick = (
    event: React.MouseEvent<
      HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | BaseButton | HTMLSpanElement,
      MouseEvent | HTMLElement
    >
  ) => {
    const target = event.target as Element;
    this.props.dispatchEditNodeChoiceSelections(
      this.props.callbackHandlers.LogHandler,
      this.props.node.get('uId'),
      this.props.node.get('dialogId')
    );
    event.stopPropagation();
  };

  private _getDynamicChoiceList = (classNames: IProcessedStyleSet<Partial<IChoiceNodeStyles>>) => {
    const dynamicChoices =
      !!this.props.dynamicChoiceList &&
      this.props.dynamicChoiceList.map(
        (dynamicChoice: IImmutableMap<Choice>, index: number): JSX.Element => {
          const text = dynamicChoice.get('text').get('content');
          const question = this.props.node?.get('question')?.get('content') ?? "";
          const isSelectedChoice = this._isChoiceSelectedChoice(dynamicChoice);
          const editIconAriaLabel = getString(
            ResourceKeys.EditIconAriaLabel,
            strings.ChoiceNode.EditIconAriaLabel,
            [text]
          );
          if (
            this.firstRender ||
            (index === this.clickedNode && this.lastClickedDynamicChoice) ||
            this.props.isInEditMode
          ) {
            return (
              <div title={text} key={text + '-tooltip' + index}>
                <DefaultButton
                  ariaLabel={question + ' ' + text}
                  role="radio"
                  key={`${this.props.uId}$dynamicChoice$${index}`}
                  text={text}
                  styles={getOptionBtnStyles({ theme: this.props.theme, stylesFunc: this.props.stylesFunc })}
                  onClick={this._onChoiceBtnClick(dynamicChoice, true, isSelectedChoice, index)}
                  allowDisabledFocus
                  toggle
                  checked={isSelectedChoice}
                  primary={isSelectedChoice}
                  aria-expanded={isSelectedChoice}
                >
                  {!!isSelectedChoice && !this.props.isInEditMode && !this.props.pauseForDiagnosticExecution &&(
                    <span className={classNames.editIconWrapper} aria-label={editIconAriaLabel} role='radio'>
                      <FontIcon
                        aria-label={editIconAriaLabel}
                        data-automation-id = {`editIcon-${text}`}
                        iconName="Edit"
                        className={classNames.editIcon}
                        key={`${this.props.uId}$staticChoiceIcon$${index}`}
                      />
                    </span>
                  )}
                </DefaultButton>
              </div>
            );
          }
        }
      );
    return dynamicChoices;
  };
  private _getStaticChoices = (classNames: IProcessedStyleSet<Partial<IChoiceNodeStyles>>) => {
    const staticChoices =
      !!this.props.staticChoices &&
      this.props.staticChoices.map(
        (staticChoice: IImmutableMap<Choice>, index: number): JSX.Element => {
          const text = staticChoice.get('text').get('content');
          const question = this.props.node?.get('question')?.get('content') ?? "";
          const isSelectedChoice = this._isChoiceSelectedChoice(staticChoice);
          const editIconAriaLabel = getString(
            ResourceKeys.EditIconAriaLabel,
            strings.ChoiceNode.EditIconAriaLabel,
            [text]
          );
          if (
            this.firstRender ||
            (index === this.clickedNode && !this.lastClickedDynamicChoice) ||
            this.props.isInEditMode
          ) {
            return (
              <div title={text} key={this.props.node.get('uId') + '-tooltip' + index}>
                <DefaultButton
                  ariaLabel={question + " " + text}
                  role="radio"
                  key={`${this.props.uId}$staticChoice$${index}`}
                  text={text}
                  styles={getOptionBtnStyles({ theme: this.props.theme, stylesFunc: this.props.stylesFunc })}
                  onClick={this._onChoiceBtnClick(staticChoice, false, isSelectedChoice, index)}
                  allowDisabledFocus
                  toggle
                  checked={this.state.isPressed}
                  primary={isSelectedChoice}
                  aria-expanded={this.state.isPressed}
                >
                  {!!isSelectedChoice && !this.props.isInEditMode && !this.props.pauseForDiagnosticExecution && (
                    <span className={classNames.editIconWrapper} aria-label={editIconAriaLabel} role='radio'>
                      <FontIcon
                        data-automation-id = {`editIcon-${text}`}
                        iconName="Edit"
                        className={classNames.editIcon}
                        aria-label={editIconAriaLabel}
                        key={`${this.props.uId}$staticChoiceIcon$${index}`}
                      />
                    </span>
                  )}
                </DefaultButton>
              </div>
            );
          }
        }
      );

    return staticChoices;
  };

  private _isChoiceSelectedChoice = (choice: IImmutableMap<Choice>) => {
    if (!!this.props.selectedChoice) {
      const sChoice = this.props.selectedChoice;
      return (
        sChoice.get('text') === choice.get('text') &&
        sChoice.get('raiseEventName') === choice.get('raiseEventName') &&
        sChoice.get('targetNodeId') === choice.get('targetNodeId')
      );
    }
  };
}

export const ChoiceNode = mconnect(
  mapStateToProps,
  mapDispatchToProps
)(styledComponent(ChoiceNodeC, getChoiceNodeStyles));
